// Safe formatter for FastAPI / network / axios error responses.
// FastAPI returns 422 validation errors as detail = [{type, loc, msg, input, ctx, url}, ...]
// 4xx/5xx may also return detail = "string" or detail = {msg: "..."}
// Network errors / 404 / 502 may have NO body at all — only err.message + err.response.status.
// React crashes if any of these objects are rendered directly. This helper always returns a short string.

const FIELD_LABELS = {
  name: 'Name',
  description: 'Short description',
  personality: 'Personality & Instructions',
  greeting: 'Greeting message',
  backstory: 'Backstory',
  avatar_url: 'Avatar',
  tags: 'Tags',
  gender: 'Gender',
  is_public: 'Visibility',
  category: 'Category',
  content: 'Content',
  email: 'Email',
  password: 'Password',
};

function prettyField(loc) {
  if (!Array.isArray(loc)) return '';
  const segs = loc.filter((s) => s !== 'body');
  const last = segs[segs.length - 1];
  return FIELD_LABELS[last] || (typeof last === 'string' ? last : '');
}

function prettyValidationItem(item) {
  if (!item || typeof item !== 'object') return '';
  const field = prettyField(item.loc);
  const raw = (item.msg || '').toString();

  if (item.type === 'string_too_short') {
    const minLen = item.ctx?.min_length;
    if (minLen) return `${field || 'This field'} must be at least ${minLen} characters.`;
  }
  if (item.type === 'string_too_long') {
    const maxLen = item.ctx?.max_length;
    if (maxLen) return `${field || 'This field'} must be ${maxLen} characters or fewer.`;
  }
  if (item.type === 'missing') return `${field || 'A required field'} is required.`;
  if (item.type === 'value_error' || item.type === 'string_pattern_mismatch') {
    return field ? `${field}: ${raw}` : raw;
  }
  return field ? `${field}: ${raw}` : raw;
}

// Generic HTTP-status-only fallback messages — short, human-readable, no JSON ever.
function statusFallback(status) {
  switch (status) {
    case 0:   return "Couldn't reach the server. Check your internet connection and try again.";
    case 400: return 'The server didn’t accept that data. Please double-check the form and try again.';
    case 401: return 'Your session expired. Please log back in and try again.';
    case 402: return 'This feature requires an upgraded plan.';
    case 403: return "You don't have permission to do that.";
    case 404: return 'The server couldn’t find what you asked for. Please try again, or contact support if it keeps happening.';
    case 408: return 'The request timed out. Please try again.';
    case 409: return 'Conflict — that already exists or was just changed. Try again.';
    case 413: return 'That payload is too large. Try a smaller image or shorter text.';
    case 422: return 'Some fields look invalid. Please check the form and try again.';
    case 429: return 'Too many requests — please slow down and try again in a moment.';
    case 500: return 'Server error. Please try again in a moment — if it keeps happening, contact support.';
    case 502:
    case 503:
    case 504: return 'The server is briefly unavailable. Please try again in a few seconds.';
    default:
      if (status >= 500) return 'Server is having issues. Please try again shortly.';
      if (status >= 400) return 'That didn’t work. Please try again.';
      return 'Something went wrong. Please try again.';
  }
}

export function formatApiError(err) {
  if (!err) return 'Something went wrong. Please try again.';

  // Network error (no response at all)
  const status = err?.response?.status ?? err?.status ?? 0;
  const body = err?.response?.data;

  // Case 1: structured FastAPI detail
  if (body && typeof body === 'object') {
    const detail = body.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const msgs = detail.map(prettyValidationItem).filter(Boolean);
      if (msgs.length) return msgs.join(' • ');
    }
    if (detail && typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
  }

  // Case 2: response body is a plain string
  if (typeof body === 'string' && body.trim() && body.length < 400) return body;

  // Case 3: fall back to a friendly status-based message
  return statusFallback(status);
}

// Tiny client-side validator for the Create Character form so users see issues
// BEFORE hitting submit (and hopefully never see a 422 again).
export function validateCharacterForm({ name, description, personality, greeting, selectedTags, avatar_url, gender }) {
  const errors = [];
  if (!name || name.trim().length < 2) errors.push('Name must be at least 2 characters.');
  if (name && name.length > 100) errors.push('Name must be 100 characters or fewer.');
  if (!description || description.trim().length < 5) errors.push('Short description must be at least 5 characters.');
  if (description && description.length > 1000) errors.push('Short description must be 1000 characters or fewer.');
  if (!greeting || greeting.trim().length < 5) errors.push('Greeting must be at least 5 characters.');
  const greetingWordCount = greeting ? greeting.trim().split(/\s+/).filter(Boolean).length : 0;
  if (greetingWordCount > 1000) errors.push('Greeting must be 1000 words or fewer.');
  if (!personality || personality.trim().length < 10) errors.push('Personality & Instructions must be at least 10 characters.');
  if (personality && personality.length > 2000) errors.push('Personality must be 2000 characters or fewer.');
  if (!gender) errors.push('Please select a gender.');
  if (!Array.isArray(selectedTags) || selectedTags.length === 0) errors.push('Pick at least 1 tag so people can find your character.');
  return errors;
}
